import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/store/auth.store";
import { Link, useNavigate } from "@tanstack/react-router";
import { LogOutIcon } from "lucide-react";
import { useSidebarActiveItems } from "./hooks/useSidebarActiveItems";
import { topItemsSidebar, bottomItemsSidebar } from "@/common/constants";
import { t } from "i18next";
import { ThemeToggle } from "@/components/ui/ThemeToggle";

interface LayoutProps {
  children?: React.ReactNode;
}

export const AppSidebar: React.FC<LayoutProps> = () => {
  const { signOut } = useAuthStore.getState();
  const navigate = useNavigate();
  const handleSignOut = async (): Promise<void> => {
    await signOut();
    await navigate({ to: "/" });
  };

  const topActiveItems = useSidebarActiveItems(topItemsSidebar);
  const bottomActiveItems = useSidebarActiveItems(bottomItemsSidebar);

  return (
    <Sidebar className="group-data-[side=left]:border-none">
      <SidebarHeader>
        <div className="flex items-center justify-center content-center w-full p-8">
          <img
            alt="ROSC"
            height={56}
            src={"/images/logos/logo-full.svg"}
            width={165}
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {topActiveItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    disabled={item.disabled}
                    isActive={item.isActive}
                  >
                    <Link to={item.url}>
                      {typeof item.icon === "string" ? (
                        <img
                          alt={item.title}
                          height={15}
                          src={item.icon}
                          width={15}
                        />
                      ) : (
                        <item.icon className="!w-auto !h-6" />
                      )}
                      {/* eslint-disable @typescript-eslint/no-explicit-any */
                      /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                      <span>{t(item.title as any)}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="flex justify-between items-center p-2">
          <span>{t("sidebar.themeToggle.darkMode")}</span>
          <ThemeToggle />
        </div>
        <SidebarSeparator />
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {bottomActiveItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      disabled={item.disabled}
                      isActive={item.isActive}
                    >
                      <Link to={item.url}>
                        {typeof item.icon === "string" ? (
                          <img
                            alt={item.title}
                            height={15}
                            src={item.icon}
                            width={15}
                          />
                        ) : (
                          <item.icon className="!w-auto !h-6" />
                        )}
                        {/* eslint-disable @typescript-eslint/no-explicit-any */
                        /* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                        <span>{t(item.title as any)}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild>
                    <Link
                      to="/"
                      onClick={() => {
                        void handleSignOut();
                      }}
                    >
                      <LogOutIcon className="!w-auto !h-6" />
                      <span>{t("sidebar.logout")}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </SidebarFooter>
    </Sidebar>
  );
};
